import { ArrowLeft, Bell, ChevronRight, ClipboardList, Compass, House, LayoutDashboard, LogOut, Moon, Search, Sun, UserRound, Wallet, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useWallets } from "../../lib/queries";
import { formatShortDate, initials, money } from "../../lib/format";
import { useTheme } from "../../theme/ThemeContext";
import { Logo } from "../Logo";
import { WelcomeTourProvider } from "../WelcomeTour";
import { PageHeaderProvider, usePageHeaderValue } from "./PageHeaderContext";
import { useHideOnScroll } from "./useHideOnScroll";

/**
 * The five sections (Inicio, Viajes, Finanzas, Perfil, Mis solicitudes). Pages that belong to a
 * section light it up: saved trips and trip pages are part of Viajes; Apartados and loans live
 * under Finanzas (the Wallet and the credit). The client asked (13 Oct 2026) for Finanzas in the
 * centre, and (22 Oct 2026) for Mis solicitudes in the iA assistant's place until the assistant has
 * functionality; /asistente still opens, it is only off the menu.
 */
const navigation = [
  { to: "/inicio", label: "Inicio", icon: House },
  { to: "/explorar", label: "Viajes", icon: Compass, also: ["/guardados", "/viajes"] },
  { to: "/wallet", label: "Finanzas", icon: Wallet, also: ["/apartados", "/credito"] },
  { to: "/perfil", label: "Perfil", icon: UserRound },
  { to: "/solicitudes", label: "Mis solicitudes", icon: ClipboardList }
];

/** Mobile tab order: Finanzas (wallet + credit) is what people use today, so it takes the prominent centre slot. */
const mobileNavigation: Array<{ to: string; label: string; short?: string; icon: LucideIcon; primary?: boolean; also?: string[] }> = [
  { to: "/inicio", label: "Inicio", icon: House },
  { to: "/explorar", label: "Viajes", icon: Compass, also: ["/guardados", "/viajes"] },
  { to: "/wallet", label: "Finanzas", icon: Wallet, primary: true, also: ["/apartados", "/credito"] },
  { to: "/perfil", label: "Perfil", icon: UserRound },
  { to: "/solicitudes", label: "Mis solicitudes", short: "Solicitudes", icon: ClipboardList }
];

export function AppShell() {
  return (
    <PageHeaderProvider>
      {/* The welcome tour opens over whichever page the customer lands on the first time. */}
      <WelcomeTourProvider>
        <ShellFrame />
      </WelcomeTourProvider>
    </PageHeaderProvider>
  );
}

const inSection = (pathname: string, prefixes: string[] = []) => prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

function ShellFrame() {
  const { user, logout } = useAuth();
  const header = usePageHeaderValue();
  const isAdmin = user?.role === "admin";
  const { pathname } = useLocation();
  const navHidden = useHideOnScroll(pathname);

  // The window is the scroll container, so without this a tab tapped from halfway down one page
  // opens the next page at the same offset (most visible on mobile: Inicio → Wallet).
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  // Bars that stick under the header (the trip page's) read its real height: it grows with a
  // subtitle and with the breakpoint, and a fixed offset let it cover them (client review, 22 Oct 2026).
  const topbarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const topbar = topbarRef.current;
    if (!topbar || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => root.style.setProperty("--topbar-height", `${topbar.offsetHeight}px`));
    observer.observe(topbar);
    return () => { observer.disconnect(); root.style.removeProperty("--topbar-height"); };
  }, []);

  return (
    <div className="app-shell">
      <aside className="side-nav" aria-label="Navegación principal">
        <div className="side-brand"><Link to="/inicio" className="side-brand-link" aria-label="Ir a Inicio" title="Inicio"><Logo height={26} /></Link></div>
        <nav>
          {navigation.map(({ to, label, icon: Icon, also }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-item${isActive || inSection(pathname, also) ? " active" : ""}`}>
              <Icon aria-hidden="true" size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <span className="nav-group">Equipo</span>
              <Link to="/admin" className="nav-item"><LayoutDashboard aria-hidden="true" size={18} /><span>Administración</span></Link>
            </>
          )}
        </nav>
        <div className="side-account">
          <NavLink to="/perfil" className="side-account-link" aria-label="Abrir perfil">
            <div className="avatar" aria-hidden="true">{initials(user?.fullName)}</div>
            <div className="account-copy"><strong>{user?.fullName}</strong><span>{user?.email}</span></div>
            <ChevronRight size={14} aria-hidden="true" />
          </NavLink>
          <button className="icon-button ghost" onClick={() => void logout()} aria-label="Cerrar sesión" title="Cerrar sesión">
            <LogOut size={17} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <div className="app-content">
        <header className="topbar" ref={topbarRef}>
          {pathname !== "/inicio" && <BackButton />}
          <div className="topbar-copy">
            <h1>{header.title}</h1>
            {header.subtitle && <span className="topbar-subtitle">{header.subtitle}</span>}
          </div>
          <div className="topbar-actions">
            {header.actions}
            <ActivityBell />
            <SearchShortcut />
            <ThemeToggle />
          </div>
        </header>
        <main className="app-main"><Outlet /></main>
      </div>

      <nav className={`bottom-nav${navHidden ? " is-hidden" : ""}`} aria-label="Navegación principal">
        {mobileNavigation.map(({ to, label, short, icon: Icon, primary, also }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `bottom-nav-item${isActive || inSection(pathname, also) ? " active" : ""}${primary ? " primary" : ""}`}>
            {primary ? <span className="main-tab"><Icon aria-hidden="true" size={20} /></span> : <Icon aria-hidden="true" size={22} />}
            <span>{short ?? label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Desktop-only "back" arrow (the client asked for one; mobile keeps the bottom tabs). Goes back through the
 * in-app history when there is some, and home otherwise — a deep link opened in a fresh tab must not leave the site.
 */
export function BackButton({ home = "/inicio" }: { home?: string }) {
  const navigate = useNavigate();
  const goBack = () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(home);
  };
  return (
    <button type="button" className="topbar-button topbar-back" onClick={goBack} aria-label="Regresar" title="Regresar">
      <ArrowLeft size={18} aria-hidden="true" />
    </button>
  );
}

export function ThemeToggle() {
  const { resolved, toggle } = useTheme();
  const dark = resolved === "dark";
  return (
    <button type="button" className="topbar-button" onClick={toggle} aria-label={dark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"} title={dark ? "Modo claro" : "Modo oscuro"} aria-pressed={dark}>
      {dark ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
    </button>
  );
}

function SearchShortcut() {
  const navigate = useNavigate();
  return (
    <button type="button" className="topbar-button" onClick={() => navigate("/explorar?buscar=1")} aria-label="Buscar destinos" title="Buscar destinos (⌘K)">
      <Search size={17} aria-hidden="true" />
    </button>
  );
}

/** Recent movements across the customer's wallets, so the bell always says something true. */
function ActivityBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const wallets = useWallets();
  const movements = (wallets.data ?? [])
    .flatMap((wallet) => wallet.transactions.map((transaction) => ({ ...transaction, product: wallet.product })))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 6);
  const pending = movements.filter((movement) => movement.status === "pending" || movement.status === "processing").length;

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", escape); };
  }, [open]);

  return (
    <div className="topbar-popover-anchor" ref={ref}>
      <button type="button" className="topbar-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-haspopup="dialog" aria-label={pending ? `Actividad · ${pending} en proceso` : "Actividad"} title="Actividad">
        <Bell size={17} aria-hidden="true" />
        {pending > 0 && <span className="topbar-dot" aria-hidden="true" />}
      </button>
      {open && (
        <div className="topbar-popover" role="dialog" aria-label="Actividad reciente">
          <span className="rail-title">Actividad reciente</span>
          {movements.length === 0 && <p className="rail-empty">Sin movimientos todavía.</p>}
          {movements.map((movement) => (
            <div className="activity-row" key={movement.id}>
              <div>
                <strong>{movement.description}</strong>
                <span>{formatShortDate(movement.createdAt)} · {movement.status === "settled" ? "Confirmado" : movement.status === "failed" ? "No completado" : "En proceso"}</span>
              </div>
              <span className={`activity-amount ${movement.signedAmount < 0 ? "negative" : "positive"}`}>
                {movement.signedAmount > 0 ? "+" : ""}{money.format(movement.signedAmount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
